import { useMemo, useState } from 'react';

import { ChevronDown, ChevronUp, MoreHorizontal } from 'lucide-react';

import DeactivateUserModal from '@/components/admin/DeactivateUserModal';
import PasswordResetModal from '@/components/admin/PasswordResetModal';
import ReactivateUserModal from '@/components/admin/ReactivateUserModal';
import EditUserModal from '@/components/admin/EditUserModal';
import RoleBadge from '@/components/admin/RoleBadge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const SORT_KEYS = {
  email: 'email',
  name: 'name',
  organization: 'organization',
  role: 'role',
  status: 'status',
  designation: 'designation',
  department: 'department',
};

const sortUsers = (users, config) => {
  if (!config) return users;
  const { key, direction } = config;
  const dir = direction === 'asc' ? 1 : -1;

  return [...users].sort((a, b) => {
    let aVal, bVal;

    switch (key) {
      case SORT_KEYS.email:
        aVal = (a.email || '').toLowerCase();
        bVal = (b.email || '').toLowerCase();
        break;
      case SORT_KEYS.name:
        aVal = (a.name || '').toLowerCase();
        bVal = (b.name || '').toLowerCase();
        break;
      case SORT_KEYS.organization:
        aVal = (a.service || '').toLowerCase();
        bVal = (b.service || '').toLowerCase();
        break;
      case SORT_KEYS.role:
        aVal = ((a.groups || ['User'])[0] || '').toLowerCase();
        bVal = ((b.groups || ['User'])[0] || '').toLowerCase();
        break;
      case SORT_KEYS.status:
        aVal = a.enabled !== false ? 0 : 1;
        bVal = b.enabled !== false ? 0 : 1;
        break;
      case SORT_KEYS.designation:
        aVal = (a.designation || '').toLowerCase();
        bVal = (b.designation || '').toLowerCase();
        break;
      case SORT_KEYS.department:
        aVal = (a.department || '').toLowerCase();
        bVal = (b.department || '').toLowerCase();
        break;
      default:
        return 0;
    }

    if (aVal < bVal) return -1 * dir;
    if (aVal > bVal) return 1 * dir;
    return 0;
  });
};

const SortIcon = ({ column, sortConfig }) => {
  if (sortConfig?.key !== column)
    return <ChevronDown className="h-4 w-4 opacity-50" />;
  if (sortConfig.direction === 'asc') return <ChevronUp className="h-4 w-4" />;
  return <ChevronDown className="h-4 w-4" />;
};

const UserTable = ({ users, onRefresh }) => {
  const [sortConfig, setSortConfig] = useState(null);
  const [passwordResetUser, setPasswordResetUser] = useState(null);
  const [roleChangeUser, setRoleChangeUser] = useState(null);
  const [deactivateUser, setDeactivateUser] = useState(null);
  const [reactivateUser, setReactivateUser] = useState(null);

  const handleSort = (column) => {
    setSortConfig((prev) => {
      if (prev && prev.key === column) {
        return { key: column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { key: column, direction: 'asc' };
    });
  };

  const sortedUsers = useMemo(
    () => sortUsers(users, sortConfig),
    [users, sortConfig]
  );

  return (
    <>
      <div className="rounded-md border">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.organization)}
                >
                  Organization
                  <SortIcon column={SORT_KEYS.organization} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.department)}
                >
                  Department
                  <SortIcon column={SORT_KEYS.department} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.email)}
                >
                  Email
                  <SortIcon column={SORT_KEYS.email} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.name)}
                >
                  Name
                  <SortIcon column={SORT_KEYS.name} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.designation)}
                >
                  Designation
                  <SortIcon column={SORT_KEYS.designation} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.role)}
                >
                  Role
                  <SortIcon column={SORT_KEYS.role} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead>
                <Button
                  variant="ghost"
                  className="h-auto p-0 font-semibold hover:bg-transparent"
                  onClick={() => handleSort(SORT_KEYS.status)}
                >
                  Status
                  <SortIcon column={SORT_KEYS.status} sortConfig={sortConfig} />
                </Button>
              </TableHead>
              <TableHead className="w-[70px]">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              sortedUsers.map((user) => (
                <TableRow key={user.id}>
                  <TableCell>{user.service || '\u2014'}</TableCell>
                  <TableCell>{user.department || '\u2014'}</TableCell>
                  <TableCell className="font-medium">{user.email}</TableCell>
                  <TableCell>{user.name || '\u2014'}</TableCell>
                  <TableCell>{user.designation || '\u2014'}</TableCell>
                  <TableCell>
                    <div className="flex flex-wrap gap-1">
                      {(user.groups || ['User']).map((role) => (
                        <RoleBadge key={role} role={role} />
                      ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    {user.enabled !== false ? (
                      <span className="text-green-600 font-medium">Active</span>
                    ) : (
                      <span className="text-red-600 font-medium">Disabled</span>
                    )}
                  </TableCell>
                  <TableCell>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" className="h-8 w-8">
                          <MoreHorizontal className="h-4 w-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => setRoleChangeUser(user)}>
                          Edit User
                        </DropdownMenuItem>
                        <DropdownMenuItem onClick={() => setPasswordResetUser(user)}>
                          Reset Password
                        </DropdownMenuItem>
                        <DropdownMenuSeparator />
                        {user.enabled !== false ? (
                          <DropdownMenuItem
                            className="text-red-600"
                            onClick={() => setDeactivateUser(user)}
                          >
                            Deactivate
                          </DropdownMenuItem>
                        ) : (
                          <DropdownMenuItem onClick={() => setReactivateUser(user)}>
                            Reactivate
                          </DropdownMenuItem>
                        )}
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </div>

      {passwordResetUser && (
        <PasswordResetModal
          open={!!passwordResetUser}
          onOpenChange={(isOpen) => {
            if (!isOpen) setPasswordResetUser(null);
          }}
          user={passwordResetUser}
        />
      )}

      {roleChangeUser && (
        <EditUserModal
          open={!!roleChangeUser}
          onOpenChange={(isOpen) => {
            if (!isOpen) setRoleChangeUser(null);
          }}
          user={roleChangeUser}
          onSuccess={onRefresh}
        />
      )}

      {deactivateUser && (
        <DeactivateUserModal
          open={!!deactivateUser}
          onOpenChange={(isOpen) => {
            if (!isOpen) setDeactivateUser(null);
          }}
          user={deactivateUser}
          onSuccess={onRefresh}
        />
      )}

      {reactivateUser && (
        <ReactivateUserModal
          open={!!reactivateUser}
          onOpenChange={(isOpen) => {
            if (!isOpen) setReactivateUser(null);
          }}
          user={reactivateUser}
          onSuccess={onRefresh}
        />
      )}
    </>
  );
};

export default UserTable;
